import { DOCUMENT } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, catchError, filter, fromEvent, map, merge, timer } from 'rxjs';

export const APP_UPDATE_CHECK_INTERVAL_MS = 60_000;

const BUNDLE_FILE = /^(?:main|polyfills|scripts|styles)(?:-[A-Za-z0-9]+)?\.(?:js|css)$/;

const bundleFingerprint = (doc: Document): string =>
	Array.from(doc.querySelectorAll('script[src], link[rel="stylesheet"][href]'))
		.map((element) => element.getAttribute('src') ?? element.getAttribute('href') ?? '')
		.filter((url) => BUNDLE_FILE.test(url))
		.sort()
		.join('|');

@Injectable({
	providedIn: 'root',
})
export class AppUpdateService {
	readonly pendingUpdate = signal(false);
	readonly showBanner = computed(() => this.pendingUpdate() && !this.bannerDismissed());

	private readonly bannerDismissed = signal(false);
	private readonly document = inject(DOCUMENT);
	private readonly destroyRef = inject(DestroyRef);
	private readonly http = new HttpClient(inject(HttpBackend));
	private readonly loadedFingerprint = bundleFingerprint(this.document);
	private started = false;

	start(): void {
		if (this.started || !this.loadedFingerprint) {
			return;
		}
		this.started = true;

		merge(timer(0, APP_UPDATE_CHECK_INTERVAL_MS), fromEvent(this.document, 'visibilitychange'))
			.pipe(
				filter(() => this.document.visibilityState === 'visible'),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => this.checkAndRemind());
	}

	dismissBanner(): void {
		this.bannerDismissed.set(true);
	}

	applyUpdate(): void {
		this.document.location.reload();
	}

	private checkAndRemind(): void {
		if (this.pendingUpdate()) {
			this.bannerDismissed.set(false);
			return;
		}

		this.http
			.get('index.html', { responseType: 'text', params: { t: Date.now() } })
			.pipe(
				map((html) => bundleFingerprint(new DOMParser().parseFromString(html, 'text/html'))),
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((fingerprint) => {
				if (fingerprint && fingerprint !== this.loadedFingerprint) {
					this.pendingUpdate.set(true);
				}
			});
	}
}
